import Box, { BoxProps } from '@material-ui/core/Box';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Typography from '@material-ui/core/Typography';
import _isEqual from 'lodash.isequal';
import * as React from 'react';
import { connect } from 'react-redux';

import AgreementsTableRow from '../components/TableRow';
import Agreement from '../models/Agreement';
import { newSelect, setAgreementsTableOrder } from '../store/actions';
import { State } from '../store/reducer';
import {
  getAgreementsTableState,
  getSelectedAgreementIdState,
  getfilteredAndSortedAgreements
} from '../store/selectors';

export interface StateProps {
  agreements: Agreement[];
  order: 'asc' | 'desc';
  orderBy: string;
  selectedAgreementId: string | null;
}

export interface DispatchProps {
  onRowClick: (id: string) => void;
  onSetOrder: (order: 'asc' | 'desc', orderBy: string) => void;
}

type Props = StateProps & DispatchProps & BoxProps;

export interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;

  cells: HeadCell[];
  order: 'asc' | 'desc';
  orderBy: string;
}

export interface HeadCell {
  id: string;
  label: string;
}

const headCells: HeadCell[] = [
  { id: 'name', label: 'Name' },
  { id: 'status', label: 'Status' },
  { id: 'facility', label: 'Facility' },
  { id: 'fileNumber', label: 'File Number' },
  { id: 'companyName', label: 'Company Name' },
  { id: 'agreementDate', label: 'Agreement Date' },
  { id: 'initialAgreementCovers', label: 'Initial Agreement Covers' },
  { id: 'notes', label: 'Notes' },
  { id: 'pipelineDiameter', label: 'Pipeline Diameter' },
  { id: 'pipelineProduct', label: 'Pipeline Product' },
  { id: 'pipelineLength', label: 'Pipeline Length' }
];

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableHeadCell: {
      padding: theme.spacing(1, 1.5, 1.5)
    },
    sortIcon: {
      height: 18,
      width: 18,
      marginLeft: theme.spacing(1),
      marginRight: 0
    }
  })
);

function EnhancedTableHead(props: EnhancedTableProps) {
  const { cells, order, orderBy, onRequestSort } = props;

  const classes = useStyles();

  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {cells.map(cell => (
          <TableCell
            className={classes.tableHeadCell}
            key={cell.id}
            sortDirection={orderBy === cell.id ? order : false}
            align={'center'}
          >
            <TableSortLabel
              active={orderBy === cell.id}
              direction={order}
              classes={{ icon: classes.sortIcon }}
              onClick={createSortHandler(cell.id)}
            >
              <Typography component="span" noWrap={true}>
                {cell.label}
              </Typography>
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export const AgreementsTable: React.FC<Props> = ({
  agreements,
  selectedAgreementId,
  onRowClick,
  order,
  orderBy,
  onSetOrder,
  ...boxProps
}) => {
  function handleRequestSort(event: React.MouseEvent<unknown>, property: string) {
    const isDesc = orderBy === property && order === 'desc';
    onSetOrder(isDesc ? 'asc' : 'desc', property);
  }

  return agreements ? (
    <Box
      display="flex"
      flexDirection="column"
      overflow="hidden"
      bgcolor="background.paper"
      {...boxProps}
    >
      <Box flex="1 1 0%" overflow="auto">
        <Table stickyHeader={true} aria-labelledby="tableTitle">
          <EnhancedTableHead
            cells={headCells}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {agreements.map(agreement => {
              return (
                <AgreementsTableRow
                  key={agreement.id}
                  agreement={agreement}
                  isSelected={agreement.id === selectedAgreementId ? true : false}
                  onClick={onRowClick}
                />
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  ) : (
    <p>No Agreements Found</p>
  );
};

export const mapState = (state: State): StateProps => {
  const agreements = getfilteredAndSortedAgreements(state);
  const { order, orderBy } = getAgreementsTableState(state);
  const selectedAgreementId = getSelectedAgreementIdState(state);
  return { agreements, order, orderBy, selectedAgreementId };
};

export const mapDispatch = {
  onRowClick: newSelect,
  onSetOrder: setAgreementsTableOrder
};

function arePropsEqual(prevProps: Props, nextProps: Props) {
  if (_isEqual(nextProps, prevProps)) {
    return true;
  }
  return false;
}

export default connect<StateProps, DispatchProps, {}, State>(
  mapState,
  mapDispatch
)(React.memo(AgreementsTable, arePropsEqual));
